import { FC, useCallback, useContext, useEffect, useState } from "react";
import { InstanceContext } from "../../context/InstanceContext";
import { fetchCrystalLicenses, ICrystalLicense } from "../../services/crystal";
import ActivateModal from "./ActivateModal";
import styles from "./index.module.css";

type ActionableStatus = Exclude<ICrystalLicense["status"], "available">;

const STATUS_LABELS: Record<ActionableStatus, string> = {
  bound: "Not Provisioned",
  configured: "Configuring",
  iso_ready: "Pending Activation",
  activated: "Activated",
};

function formatCreatedTs(ts: number): string {
  const d = new Date(ts * 1000);
  const m = d.getMonth() + 1;
  const day = d.getDate();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${m}/${day} ${hh}:${mm}`;
}

const ExternalLinkIcon: FC = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 4, verticalAlign: "middle" }}>
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const CrystalLicenses: FC = () => {
  const { instancesWithRole } = useContext(InstanceContext);

  const [licenses, setLicenses] = useState<ICrystalLicense[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activatingId, setActivatingId] = useState<string | null>(null);

  const loadLicenses = useCallback(async () => {
    try {
      setLoading(true);
      const { licenses: data } = await fetchCrystalLicenses();
      setLicenses(data.filter((l) => l.status !== "available"));
    } catch (err: any) {
      setError(err?.message || "Failed to load licenses.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLicenses();
  }, [loadLicenses]);

  const getManagedByDomain = (mspId: string | null): string | null => {
    if (!mspId) return null;
    return instancesWithRole.find((i) => i.id === mspId)?.domain ?? null;
  };

  return (
    <div className={styles["page"]}>
      <div className={styles["container"]}>
        <div className={styles["card"]}>
          <div className={styles["card-header"]}>
            <h2 className={styles["title"]}>Crystal Licenses</h2>
            <button className={styles["buy-btn"]}>Buy New License</button>
          </div>
          <div className={styles["table-wrap"]}>
            <table className={styles["table"]}>
              <thead>
                <tr>
                  <th>Device Name</th>
                  <th>Managed By</th>
                  <th>Status</th>
                  <th>Action</th>
                  <th>
                    Created Time
                    <span className={styles["sort-icon"]}>⇅</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr className={styles["loading-row"]}>
                    <td colSpan={5}>Loading licenses…</td>
                  </tr>
                )}
                {!loading && error && (
                  <tr className={styles["error-row"]}>
                    <td colSpan={5}>{error}</td>
                  </tr>
                )}
                {!loading && !error && licenses.length === 0 && (
                  <tr className={styles["loading-row"]}>
                    <td colSpan={5}>No licenses found.</td>
                  </tr>
                )}
                {!loading && !error && licenses.map((license) => {
                  const status = license.status as ActionableStatus;
                  const domain = getManagedByDomain(license.msp_id);

                  return (
                    <tr key={license.license_id}>
                      <td>{license.license_type ?? license.license_id.slice(0, 8)}</td>
                      <td>
                        {domain ? (
                          <a
                            href={`https://${domain}`}
                            target="_blank"
                            rel="noreferrer"
                            className={styles["managed-link"]}
                          >
                            {domain}
                            <ExternalLinkIcon />
                          </a>
                        ) : (
                          <span className={styles["empty"]}>—</span>
                        )}
                      </td>
                      <td>
                        <span className={`${styles["status"]} ${styles[`status-${status}`]}`}>
                          <span className={styles["status-dot"]} />
                          {STATUS_LABELS[status]}
                        </span>
                      </td>
                      <td className={styles["action-cell"]}>
                        <button
                          className={styles["action-link"]}
                          onClick={() => setActivatingId(license.license_id)}
                        >
                          {status === "bound" ? "Provision" : "Re-Provision"}
                        </button>
                      </td>
                      <td>{license.bound_ts ? formatCreatedTs(license.bound_ts) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {activatingId && (
        <ActivateModal
          licenseId={activatingId}
          onClose={() => setActivatingId(null)}
        />
      )}
    </div>
  );
};

export default CrystalLicenses;
